import React, {createContext, Dispatch, ReactNode, useCallback, useContext, useMemo, useReducer, useState} from 'react';
import Template, {Design, ImageValue} from "../models/Template";
import {Scene, SceneTransforms} from "../scene/types";
import {deriveImageFit, ImageFitMap} from "../scene/imageFit";
import {
    designKeyOf,
    layerImageFit,
    mergeUserLayers,
    UserLayersAction,
    UserLayersByDesign,
    userLayersReducer,
} from "../scene/userLayers";
import {DEFAULT_VISIBILITY, Visibility, visibleScene} from "../scene/rowVisibility";

type ImageTransformations = {
    [key: string]: {
        left: number;
        top: number;
        width: number;
        height: number;
        transformed: boolean;
        reset?: true; // indicates if the transform came from reset.
    };
}

export interface SelectedImageData {
    elementKey: string;
    label: string;
    value: ImageValue;
    folderId: string;
}

// Define the shape of the context data
interface TemplateContextType {
    template: Template | undefined;
    setTemplate: React.Dispatch<React.SetStateAction<Template | undefined>>;
    selections: {
        [key: string]: string | ImageValue
    };
    setSelections: React.Dispatch<React.SetStateAction<{
        [key: string]: string | ImageValue
    }>>;
    selectedContentEditable: string | undefined;
    setSelectedContentEditable: React.Dispatch<React.SetStateAction<string | undefined>>;
    imageTransforms: ImageTransformations;
    setImageTransforms: React.Dispatch<React.SetStateAction<ImageTransformations>>;
    selectedTransformedImage: string | undefined;
    setSelectedTransformedImage: React.Dispatch<React.SetStateAction<string | undefined>>;
    /** Row reposition mode: the key of the row (Scene.rows) being moved as one
     *  unit, or null. Mutually exclusive with `selectedTransformedImage`. */
    repositioningRow: string | null;
    setRepositioningRow: React.Dispatch<React.SetStateAction<string | null>>;
    resetTemplateContext: () => void;
    selectedImageData: SelectedImageData | undefined;
    setSelectedImageData: React.Dispatch<React.SetStateAction<SelectedImageData | undefined>>;
    // Canvas ("scene") engine inline editing:
    sceneTransforms: SceneTransforms;
    setSceneTransforms: React.Dispatch<React.SetStateAction<SceneTransforms>>;
    selectedSceneId: string | null;
    setSelectedSceneId: React.Dispatch<React.SetStateAction<string | null>>;
    /** DERIVED, never set: image fields whose value the user replaced, which
     *  render contained inside the field box instead of cover-cropped. One
     *  memo for the whole app — the preview (TemplateDisplay -> SceneRenderer)
     *  and the export payload (useImageProcessor) must not disagree. Image USER
     *  LAYERS are folded in here too (they always contain). */
    imageFit: ImageFitMap;
    /** End-user-added text/image layers, per design key. Only meaningful when
     *  `template.userLayersEnabled === true`; every reader gates on that flag,
     *  so a template that is switched off simply stops rendering them. */
    userLayersByDesign: UserLayersByDesign;
    dispatchUserLayers: Dispatch<UserLayersAction>;
    /** `template.userLayersEnabled === true` — the ONE place the opt-in flag is
     *  read, so add UI / merge / draft / export can never disagree about it. */
    userLayersEnabled: boolean;
    /** Hidden rows and flipped switches (scene/rowVisibility). */
    visibility: Visibility;
    setVisibility: React.Dispatch<React.SetStateAction<Visibility>>;
}

// Create the context with a default value
export const TemplateContext = createContext<TemplateContextType>({
    template: undefined,
    setTemplate: () => {
    }, // Empty function for default
    selections: {},
    setSelections: () => {
    }, // Empty function for default
    selectedContentEditable: undefined,
    setSelectedContentEditable: () => {
    }, // Empty function for default
    imageTransforms: {},
    setImageTransforms: () => {
    }, // Empty function for default
    selectedTransformedImage: undefined,
    setSelectedTransformedImage: () => {
    }, // Empty function for default
    repositioningRow: null,
    setRepositioningRow: () => {
    },
    resetTemplateContext: () => {
    }, // Empty function for default
    selectedImageData: undefined,
    setSelectedImageData: () => {
    },
    sceneTransforms: {},
    setSceneTransforms: () => {
    },
    selectedSceneId: null,
    setSelectedSceneId: () => {
    },
    imageFit: {},
    userLayersByDesign: {},
    dispatchUserLayers: () => {
    },
    userLayersEnabled: false,
    visibility: DEFAULT_VISIBILITY,
    setVisibility: () => {
    },
});

// Actions-only context: exposes just the setters + resetTemplateContext, all of
// which are stable for the Provider's lifetime. A consumer that only needs to
// *write* context can subscribe here and never re-render on state changes. Kept
// separate on purpose — existing consumers stay on useTemplate() unchanged.
interface TemplateActionsContextType {
    setTemplate: TemplateContextType['setTemplate'];
    setSelections: TemplateContextType['setSelections'];
    setSelectedContentEditable: TemplateContextType['setSelectedContentEditable'];
    setImageTransforms: TemplateContextType['setImageTransforms'];
    setSelectedTransformedImage: TemplateContextType['setSelectedTransformedImage'];
    setRepositioningRow: TemplateContextType['setRepositioningRow'];
    setSelectedImageData: TemplateContextType['setSelectedImageData'];
    setSceneTransforms: TemplateContextType['setSceneTransforms'];
    setSelectedSceneId: TemplateContextType['setSelectedSceneId'];
    dispatchUserLayers: TemplateContextType['dispatchUserLayers'];
    setVisibility: TemplateContextType['setVisibility'];
    resetTemplateContext: () => void;
}

export const TemplateActionsContext = createContext<TemplateActionsContextType>({
    setTemplate: () => {
    },
    setSelections: () => {
    },
    setSelectedContentEditable: () => {
    },
    setImageTransforms: () => {
    },
    setSelectedTransformedImage: () => {
    },
    setRepositioningRow: () => {
    },
    setSelectedImageData: () => {
    },
    setSceneTransforms: () => {
    },
    setSelectedSceneId: () => {
    },
    dispatchUserLayers: () => {
    },
    setVisibility: () => {
    },
    resetTemplateContext: () => {
    },
});

interface TemplateProviderProps {
    children: ReactNode;
}

// Create a provider for components to consume and subscribe to changes
export const TemplateProvider: React.FC<TemplateProviderProps> = ({children}) => {
    const [template, setTemplate] = useState<Template>();
    const [selections, setSelections] = useState<{
        [key: string]: string | ImageValue
    }>({});
    const [selectedContentEditable, setSelectedContentEditable] = useState<string | undefined>();
    /**
     * Each image’s transform inside its clip container:
     *   left, top => where the image is moved inside the container
     *   width, height => actual size or scaled size of the image
     */
    const [imageTransforms, setImageTransforms] = useState<ImageTransformations>({});
    const [selectedTransformedImage, setSelectedTransformedImage] = useState<string | undefined>();
    const [repositioningRow, setRepositioningRow] = useState<string | null>(null);
    const [selectedImageData, setSelectedImageData] = useState<SelectedImageData | undefined>();
    const [sceneTransforms, setSceneTransforms] = useState<SceneTransforms>({});
    const [selectedSceneId, setSelectedSceneId] = useState<string | null>(null);
    const [userLayersByDesign, dispatchUserLayers] = useReducer(userLayersReducer, {});
    const userLayersEnabled = template?.userLayersEnabled === true;
    const [visibility, setVisibility] = useState<Visibility>(DEFAULT_VISIBILITY);

    // Every entry point that can change an image value (gallery pick, upload,
    // Fill Mode quick upload, AI result, Excel import, draft hydration) writes
    // `selections`, so deriving here covers all of them and none of them has to
    // remember to stamp anything.
    const imageFit = useMemo(
        () => ({
            ...deriveImageFit(template?.inputs, selections),
            // An image LAYER has no template default to compare against — the
            // user picked every pixel of it, so it always contains.
            ...layerImageFit(userLayersByDesign, userLayersEnabled),
        }),
        [template?.inputs, selections, userLayersByDesign, userLayersEnabled],
    );

    // Stable across renders — the state setters it calls are stable by React
    // contract, so no deps are needed.
    const resetTemplateContext = useCallback(() => {
        setImageTransforms({})
        setSelectedTransformedImage(undefined)
        setRepositioningRow(null)
        setSelectedContentEditable(undefined)
        setSelectedImageData(undefined)
        setSelections({})
        setSceneTransforms({})
        setSelectedSceneId(null)
        dispatchUserLayers({type: 'reset'})
        setVisibility(DEFAULT_VISIBILITY)
        setTemplate(undefined)
    }, [])

    // Memoized so the Provider hands down a stable reference whenever none of the
    // 8 state values changed. Setters + resetTemplateContext are all stable.
    const value = useMemo<TemplateContextType>(() => ({
        template,
        setTemplate,
        selections,
        setSelections,
        selectedContentEditable,
        setSelectedContentEditable,
        imageTransforms,
        setImageTransforms,
        selectedTransformedImage,
        setSelectedTransformedImage,
        repositioningRow,
        setRepositioningRow,
        resetTemplateContext,
        selectedImageData,
        setSelectedImageData,
        sceneTransforms,
        setSceneTransforms,
        selectedSceneId,
        setSelectedSceneId,
        imageFit,
        userLayersByDesign,
        dispatchUserLayers,
        userLayersEnabled,
        visibility,
        setVisibility,
    }), [
        template,
        selections,
        selectedContentEditable,
        imageTransforms,
        selectedTransformedImage,
        repositioningRow,
        selectedImageData,
        sceneTransforms,
        selectedSceneId,
        imageFit,
        userLayersByDesign,
        userLayersEnabled,
        visibility,
        resetTemplateContext,
    ]);

    // Everything here is stable for the Provider's lifetime, so memoize once —
    // this context never re-renders its consumers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const actions = useMemo<TemplateActionsContextType>(() => ({
        setTemplate,
        setSelections,
        setSelectedContentEditable,
        setImageTransforms,
        setSelectedTransformedImage,
        setRepositioningRow,
        setSelectedImageData,
        setSceneTransforms,
        setSelectedSceneId,
        dispatchUserLayers,
        setVisibility,
        resetTemplateContext,
    }), []);

    return (
        <TemplateContext.Provider value={value}>
            <TemplateActionsContext.Provider value={actions}>
                {children}
            </TemplateActionsContext.Provider>
        </TemplateContext.Provider>
    );
};


// Hook to use the context
export const useTemplate = () => {
    const context = useContext(TemplateContext);
    if (!context) {
        throw new Error('useTemplate must be used within a TemplateProvider');
    }
    return context;
};

/**
 * The scene a surface should RENDER for one design: the template scene with this
 * design's user layers spliced in, minus hidden rows and switched-off layers
 * (docs/row-groups.md §4). Memoized on its inputs so the object identity
 * only changes when something in it actually did — SceneRenderer resets its
 * image-load accounting and re-preloads fonts on every new scene object.
 *
 * This is the ONLY place a merged scene is built in the editor; the export path
 * (useImageProcessor) calls the same pure `mergeUserLayers` + `visibleScene`
 * per design index because it cannot use a hook inside its callback.
 */
export const useMergedScene = (
    design: Design | undefined | null,
    designIndex: number | undefined,
): Scene | undefined => {
    const {selections, userLayersByDesign, userLayersEnabled, template, visibility} = useTemplate();
    const designKey = designKeyOf(design, designIndex);
    const layers = userLayersByDesign[designKey];

    // `selections` changes on EVERY keystroke, but the only thing the merge
    // reads out of it is an image layer's src + intrinsic size (a text layer's
    // string reaches Konva through SceneRenderer's `values`, not through the
    // scene). Depending on the whole map would hand SceneRenderer a new scene
    // object per character typed, which resets its image-load accounting and
    // re-runs the font preload — visible as the canvas flickering while typing.
    const imageLayerFingerprint = (layers ?? [])
        .filter((l) => l.kind === 'image')
        .map((l) => {
            const v = selections[l.id];
            return v && typeof v === 'object' ? `${l.id}:${v.highRes}:${v.width}x${v.height}` : `${l.id}:`;
        })
        .join('|');

    // Read through a ref so the merge always sees the CURRENT values without
    // making every keystroke a dependency.
    const selectionsRef = React.useRef(selections);
    selectionsRef.current = selections;

    const merged = useMemo(
        () => mergeUserLayers(design?.scene, layers, selectionsRef.current as any, userLayersEnabled),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [design?.scene, layers, imageLayerFingerprint, userLayersEnabled],
    );
    // AFTER the merge: a user layer's `order` is an absolute index into the
    // template's elements, so dropping elements first would restack it.
    return useMemo(
        () => (merged ? visibleScene(merged, template?.toggles, visibility) : undefined),
        [merged, template?.toggles, visibility],
    );
};

// Hook for consumers that only need the setters / reset (no re-render on state).
export const useTemplateActions = () => {
    const context = useContext(TemplateActionsContext);
    if (!context) {
        throw new Error('useTemplateActions must be used within a TemplateProvider');
    }
    return context;
};